import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUploadOutlined as ExportIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  DescriptionOutlined as GoogleDocIcon,
  InsertDriveFileOutlined as DriveFileIcon,
  Link as LinkIcon,
  SlideshowOutlined as SlidesIcon,
  TableChartOutlined as SheetsIcon,
  GridOn as GridOnIcon,
  LinearScale as LinearScaleIcon,
  Adjust as AdjustIcon,
  Rule as RuleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  asDocumentContent,
  asLinkUrl,
  asRubricContent,
  asSlideContent,
  createEmptyDocumentContent,
  createEmptySlideContent,
  normalizeExternalResourceUrl,
  type CreateResourceRequest,
  type FileResourceContent,
  type Resource,
  type ResourceParentLocator,
  type ResourceTemplate,
  GenerateFromTemplateRequest,
  GenerateFromTemplateResult,
} from '@/shared/types/resource';
import { SortableList, type SortableListRenderProps } from '@/shared/components/SortableList';
import { getReorderAnchorPair, type ReorderPlacement } from '@/shared/utils/reorder';
import { useConfirm } from '@/shared/components/ConfirmProvider';
import { useAppSelector } from '@/shared/hooks/redux';
import UnifiedResourcePickerDialog from '@/features/resources/components/UnifiedResourcePickerDialog';
import CreateRubricWizard from '@/features/rubrics/components/CreateRubricWizard';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { countImagePlaceholders } from '@/shared/utils/documentImagePlaceholders';
import { useMoveResourceMutation } from '@/features/resources/resourcesApi';
import {
  uploadFile,
  deleteFile,
  extFromMime,
  UPLOADED_FILES_ACCEPT,
  UPLOADED_FILES_MAX_SIZE,
} from '@/shared/services/storageService';

interface ResourceListCardProps {
  locator: ResourceParentLocator;
  resources: Resource[];
  templates?: ResourceTemplate[];
  canEdit?: boolean;
  title?: string;
  onCreateResource?: (
    resource: CreateResourceRequest
  ) => Promise<Resource | void> | Resource | void;
  onCreateFromTemplate?: (templateId: string) => Promise<Resource | void> | Resource | void;
  onGenerateFromTemplate?: (
    request: GenerateFromTemplateRequest
  ) => Promise<GenerateFromTemplateResult | void>;
  onUpdateResource?: (resource: Resource) => Promise<void> | void;
  onDelete?: (resourceId: string) => Promise<void> | void;
  onDuplicateResource?: (resource: Resource) => Promise<Resource | void> | Resource | void;
  onExportToGoogle?: (resource: Resource) => Promise<void> | void;
  disableGenerate?: boolean;
}

const ResourceListCard: React.FC<ResourceListCardProps> = ({
  locator,
  resources,
  templates = [],
  canEdit = false,
  title = 'Resources',
  onCreateResource,
  onCreateFromTemplate,
  onGenerateFromTemplate,
  onUpdateResource,
  onDelete,
  onDuplicateResource,
  onExportToGoogle,
  disableGenerate = false,
}) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAppSelector((state) => state.auth);
  const googleSlidesImportEnabled = isFeatureEnabled('googleSlidesImport');
  const googleDocsImportEnabled = isFeatureEnabled('googleDocsImport');
  const rubricsEnabled = isFeatureEnabled('rubrics');
  const [moveResource] = useMoveResourceMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rubricWizardOpen, setRubricWizardOpen] = useState(false);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [exportingResourceId, setExportingResourceId] = useState<string | null>(null);
  const [googleDriveError, setGoogleDriveError] = useState<string | null>(null);
  const [nativeImportInfo, setNativeImportInfo] = useState<string | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    resourceKind: 'link' as 'link' | 'document' | 'slide' | 'file',
    title: '',
    description: '',
    url: '',
    documentBody: '',
  });

  const handleMoveResource = async (resourceId: string, placement: ReorderPlacement) => {
    const anchors = getReorderAnchorPair(resources, resourceId, placement);
    if (!anchors) return;

    await moveResource({ resourceId, ...locator, placement, anchors }).unwrap();
  };

  const openCreateDialog = (kind: 'link' | 'document' | 'slide' | 'file' = 'link') => {
    setEditing(null);
    setSelectedFile(null);
    setFileUploadError(null);
    setFormData({
      resourceKind: kind,
      title: '',
      description: '',
      url: '',
      documentBody: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (resource: Resource) => {
    if (resource.resourceKind === 'rubric') {
      navigate(`/curriculum/resources/${resource.id}/rubric`);
      return;
    }
    const linkUrl = normalizeExternalResourceUrl(asLinkUrl(resource)) ?? '';
    setEditing(resource);
    setFormData({
      resourceKind:
        resource.resourceKind === 'document' || resource.resourceKind === 'slide'
          ? resource.resourceKind
          : 'link',
      title: resource.title,
      description: resource.description ?? '',
      url: linkUrl,
      documentBody: '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setSelectedFile(null);
    setFileUploadError(null);
    setFileUploading(false);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    const nextTitle = formData.title.trim();
    const nextDescription = formData.description || null;

    if (editing && onUpdateResource) {
      if (editing.resourceKind === 'document') {
        const currentDoc = asDocumentContent(editing) ?? createEmptyDocumentContent(nextTitle);
        await onUpdateResource({
          ...editing,
          title: nextTitle,
          description: nextDescription,
          content: {
            ...currentDoc,
            title: nextTitle,
          },
        });
      } else if (editing.resourceKind === 'slide') {
        const currentSlides = asSlideContent(editing) ?? createEmptySlideContent(nextTitle);
        await onUpdateResource({
          ...editing,
          title: nextTitle,
          description: nextDescription,
          content: {
            ...currentSlides,
            presentation: {
              ...currentSlides.presentation,
              title: nextTitle,
            },
          },
        });
      } else if (editing.resourceKind === 'link') {
        await onUpdateResource({
          ...editing,
          title: nextTitle,
          description: nextDescription,
          content: { url: normalizeExternalResourceUrl(formData.url) ?? '' },
        });
      } else {
        await onUpdateResource({
          ...editing,
          title: nextTitle,
          description: nextDescription,
          content: editing.content,
        });
      }
      closeDialog();
      return;
    }

    if (!onCreateResource) return;
    let createdResource: Resource | void;
    if (formData.resourceKind === 'document') {
      createdResource = await onCreateResource({
        resourceKind: 'document',
        title: nextTitle,
        description: formData.description || null,
        content: {
          ...createEmptyDocumentContent(nextTitle),
          tiptapDoc: plainTextToTiptapDoc(formData.documentBody),
        },
      });
    } else if (formData.resourceKind === 'slide') {
      createdResource = await onCreateResource({
        resourceKind: 'slide',
        title: nextTitle,
        description: formData.description || null,
        content: createEmptySlideContent(nextTitle || 'Untitled Presentation'),
      });
    } else if (formData.resourceKind === 'file') {
      if (!selectedFile || !user) return;
      if (!onUpdateResource || !onDelete) {
        setFileUploadError('File uploads are not configured for this resource list.');
        return;
      }
      setFileUploading(true);
      setFileUploadError(null);
      let createdFileResource: Resource | null = null;
      let uploadedStoragePath: string | null = null;
      try {
        // Step 1: Create resource with placeholder content
        const placeholderContent: FileResourceContent = {
          storageBucket: 'uploaded-files',
          storagePath: '',
          originalFilename: selectedFile.name,
          mimeType: selectedFile.type || 'application/octet-stream',
          fileSize: selectedFile.size,
        };
        createdResource = await onCreateResource({
          resourceKind: 'file',
          title: nextTitle,
          description: formData.description || null,
          content: placeholderContent,
        });
        if (!createdResource) throw new Error('Failed to create resource');
        createdFileResource = createdResource;

        // Step 2: Upload file
        const ext = extFromMime(selectedFile.type);
        const { storagePath } = await uploadFile(
          'uploaded-files',
          user.id,
          createdResource.id,
          selectedFile,
          ext
        );
        uploadedStoragePath = storagePath;

        // Step 3: Update resource content with real storage path
        const fileContent: FileResourceContent = {
          storageBucket: 'uploaded-files',
          storagePath,
          originalFilename: selectedFile.name,
          mimeType: selectedFile.type || 'application/octet-stream',
          fileSize: selectedFile.size,
        };
        if (!onUpdateResource) throw new Error('Unable to attach uploaded file to resource.');
        await onUpdateResource({ ...createdResource, content: fileContent });
      } catch (err) {
        console.error('File upload failed', err);
        if (createdFileResource) {
          try {
            await onDelete(createdFileResource.id);
          } catch (cleanupErr) {
            console.error('Failed to delete orphaned file resource', cleanupErr);
          }
        }
        if (uploadedStoragePath) {
          try {
            await deleteFile('uploaded-files', uploadedStoragePath);
          } catch (cleanupErr) {
            console.error('Failed to delete orphaned storage file', cleanupErr);
          }
        }
        setFileUploadError(err instanceof Error ? err.message : 'File upload failed.');
        setFileUploading(false);
        return;
      }
      setFileUploading(false);
    } else {
      if (!formData.url.trim()) return;
      createdResource = await onCreateResource({
        resourceKind: 'link',
        title: nextTitle,
        description: formData.description || null,
        content: { url: normalizeExternalResourceUrl(formData.url) ?? '' },
      });
    }
    closeDialog();
    openCreatedResource(createdResource);
  };

  const handleDelete = async (resourceId: string) => {
    if (!canEdit || !onDelete) return;
    const ok = await confirm({
      title: 'Delete resource',
      message: 'Delete this resource? This cannot be undone.',
      confirmButtonText: 'Delete',
    });
    if (ok) await onDelete(resourceId);
  };

  const handleOpen = (resource: Resource) => {
    const editorPath = getResourceEditorPath(resource);
    if (editorPath) {
      navigate(editorPath);
      return;
    }

    const link = normalizeExternalResourceUrl(asLinkUrl(resource));
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const openCreatedResource = (resource: Resource | void) => {
    if (!resource) return;
    const editorPath = getResourceEditorPath(resource);
    if (editorPath) {
      navigate(editorPath);
    }
  };

  const openGeneratedDocumentResource = (result: GenerateFromTemplateResult) => {
    const editorPath = getResourceEditorPath({
      id: result.resourceId,
      resourceKind: result.resourceKind,
    } as Resource);
    if (editorPath) navigate(editorPath);
  };

  const handleDuplicate = async (resource: Resource) => {
    if (!onDuplicateResource) return;
    const createdResource = await onDuplicateResource(resource);
    openCreatedResource(createdResource);
  };

  const handleExportToGoogle = async (resource: Resource) => {
    if (!onExportToGoogle || exportingResourceId) return;

    if (resource.resourceKind === 'document') {
      const doc = asDocumentContent(resource);
      const unresolvedCount = countImagePlaceholders(doc?.tiptapDoc);

      if (unresolvedCount > 0) {
        const ok = await confirm({
          title: 'Export with pending images?',
          message: `${unresolvedCount} image placeholder${unresolvedCount === 1 ? '' : 's'} will appear as "[Image: search term]" text in the exported Google Doc. Export anyway?`,
          confirmButtonText: 'Export anyway',
        });

        if (!ok) return;
      }
    }

    setExportingResourceId(resource.id);
    try {
      await onExportToGoogle(resource);
    } finally {
      setExportingResourceId(null);
    }
  };

  const showUrlField = editing ? editing.resourceKind === 'link' : formData.resourceKind === 'link';
  const showFileField = !editing && formData.resourceKind === 'file';
  const showContentEditHint =
    editing && (editing.resourceKind === 'document' || editing.resourceKind === 'slide');
  const isSaveDisabled =
    !formData.title.trim() ||
    (showUrlField && !formData.url.trim()) ||
    (showFileField && !selectedFile) ||
    fileUploading;

  return (
    <Card>
      <CardHeader
        title={title}
        sx={{
          pt: 1,
          pb: 0,
          '&& .MuiCardHeader-title': {
            fontSize: '1.5rem',
            lineHeight: 1.3,
          },
        }}
      />
      <CardContent sx={{ py: 0, mb: 0 }}>
        {googleDriveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {googleDriveError}
          </Alert>
        )}

        {nativeImportInfo && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNativeImportInfo(null)}>
            {nativeImportInfo}
          </Alert>
        )}

        {resources.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No resources yet.
          </Typography>
        ) : (
          <SortableList
            items={resources}
            canEdit={canEdit}
            onMove={handleMoveResource}
            getItemLabel={(resource) => `Reorder ${resource.title}`}
            reorderErrorMessage="Failed to reorder resource. Please try again."
            renderContainer={(children) => <List dense>{children}</List>}
            renderItem={(resource, dragProps) => (
              <SortableResourceItem
                resource={resource}
                canEdit={canEdit}
                exportingResourceId={exportingResourceId}
                onOpen={handleOpen}
                onEdit={canEdit ? openEditDialog : undefined}
                onDelete={canEdit && onDelete ? handleDelete : undefined}
                onDuplicate={canEdit && onDuplicateResource ? handleDuplicate : undefined}
                onExportToGoogle={canEdit && onExportToGoogle ? handleExportToGoogle : undefined}
                dragProps={dragProps}
              />
            )}
          />
        )}
      </CardContent>
      {canEdit && (
        <CardActions sx={{ justifyContent: 'center', m: 0, pt: 0 }}>
          <Button
            sx={{ m: 0 }}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setPickerOpen(true)}
          >
            Add Resource
          </Button>
        </CardActions>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
        <DialogContent>
          {editing ? (
            <TextField
              margin="dense"
              label="Resource Type"
              fullWidth
              value={getResourceKindLabel(editing.resourceKind)}
              disabled
              sx={{ mb: 2 }}
            />
          ) : (
            <TextField
              select
              margin="dense"
              label="Resource Type"
              fullWidth
              value={formData.resourceKind}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  resourceKind: event.target.value as 'link' | 'document' | 'slide' | 'file',
                }))
              }
              sx={{ mb: 2 }}
            >
              <MenuItem value="link">Link</MenuItem>
              <MenuItem value="document">Document</MenuItem>
              <MenuItem value="slide">Slide Deck</MenuItem>
              <MenuItem value="file">File Upload</MenuItem>
            </TextField>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={formData.title}
            onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, description: event.target.value }))
            }
            sx={{ mb: 2 }}
          />
          {showContentEditHint ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use Open to edit the{' '}
              {editing?.resourceKind === 'document' ? 'document content' : 'slides'}.
            </Typography>
          ) : null}
          {showUrlField ? (
            <TextField
              margin="dense"
              label="URL"
              fullWidth
              placeholder="https://example.com"
              value={formData.url}
              onChange={(event) => setFormData((prev) => ({ ...prev, url: event.target.value }))}
            />
          ) : null}
          {showFileField && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                component="label"
                disabled={fileUploading}
                startIcon={fileUploading ? <CircularProgress size={16} /> : <DriveFileIcon />}
              >
                {selectedFile ? selectedFile.name : 'Choose file'}
                <input
                  type="file"
                  hidden
                  accept={UPLOADED_FILES_ACCEPT.join(',')}
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    if (file && file.size > UPLOADED_FILES_MAX_SIZE) {
                      setFileUploadError(
                        `File exceeds the ${UPLOADED_FILES_MAX_SIZE / (1024 * 1024)} MB limit.`
                      );
                      setSelectedFile(null);
                    } else {
                      setFileUploadError(null);
                      setSelectedFile(file);
                      if (file && !formData.title.trim()) {
                        setFormData((prev) => ({
                          ...prev,
                          title: file.name.replace(/\.[^.]+$/, ''),
                        }));
                      }
                    }
                    event.target.value = '';
                  }}
                />
              </Button>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {(selectedFile.size / 1024).toFixed(0)} KB
                </Typography>
              )}
              {fileUploadError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {fileUploadError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={isSaveDisabled}
            startIcon={fileUploading ? <CircularProgress size={16} /> : undefined}
          >
            {fileUploading ? 'Uploading...' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <UnifiedResourcePickerDialog
        open={pickerOpen}
        locator={locator}
        templates={templates}
        disableGenerate={disableGenerate}
        rubricsEnabled={rubricsEnabled}
        showGoogleDrive={Boolean(locator.courseId)}
        showImportSlides={Boolean(locator.courseId) && googleSlidesImportEnabled}
        showImportDocs={Boolean(locator.courseId) && googleDocsImportEnabled}
        onClose={() => setPickerOpen(false)}
        onPickTemplate={async ({ templateId }) => onCreateFromTemplate?.(templateId)}
        onCreatedResource={openCreatedResource}
        onGenerateFromTemplate={async (request) => {
          if (!onGenerateFromTemplate) return;
          return onGenerateFromTemplate(request);
        }}
        onGeneratedResource={openGeneratedDocumentResource}
        onNewDocument={() => {
          setPickerOpen(false);
          openCreateDialog('document');
        }}
        onNewSlides={() => {
          setPickerOpen(false);
          openCreateDialog('slide');
        }}
        onNewRubric={() => {
          setPickerOpen(false);
          setRubricWizardOpen(true);
        }}
        onUploadFile={() => {
          setPickerOpen(false);
          openCreateDialog('file');
        }}
        onAddLink={() => {
          setPickerOpen(false);
          openCreateDialog('link');
        }}
        onGoogleDriveError={setGoogleDriveError}
        onNativeImportComplete={({ totalWarnings, importedCount, failedCount }) => {
          if (importedCount === 0) {
            setNativeImportInfo(null);
            return;
          }
          const importedMessage = `Imported ${importedCount} presentation${importedCount !== 1 ? 's' : ''}`;
          const warningMessage =
            totalWarnings > 0
              ? ` with ${totalWarnings} warning${totalWarnings !== 1 ? 's' : ''} (some elements may not have imported perfectly)`
              : '';
          const failureMessage =
            failedCount > 0 ? `; ${failedCount} file${failedCount !== 1 ? 's' : ''} failed` : '';
          setNativeImportInfo(`${importedMessage}${warningMessage}${failureMessage}.`);
        }}
      />

      {rubricsEnabled && (
        <CreateRubricWizard
          open={rubricWizardOpen}
          onClose={() => setRubricWizardOpen(false)}
          attachTo={locator}
        />
      )}
    </Card>
  );
};

function getResourceEditorPath(resource: Resource): string | null {
  if (resource.resourceKind === 'document') {
    return `/curriculum/resources/${resource.id}/edit`;
  }

  if (resource.resourceKind === 'slide') {
    return `/curriculum/resources/${resource.id}/slides`;
  }

  if (resource.resourceKind === 'file') {
    return `/curriculum/resources/${resource.id}/file`;
  }

  if (resource.resourceKind === 'rubric') {
    return `/curriculum/resources/${resource.id}/rubric`;
  }

  return null;
}

type ResourceRowDisplay = {
  chipLabel: string;
  chipColor: 'default' | 'info' | 'success' | 'warning';
  chipIcon: React.ReactElement;
  secondaryText: string;
  openTooltip: string;
};

interface SortableResourceItemProps {
  resource: Resource;
  canEdit: boolean;
  exportingResourceId: string | null;
  onOpen: (resource: Resource) => void;
  onEdit?: (resource: Resource) => void;
  onDelete?: (resourceId: string) => Promise<void> | void;
  onDuplicate?: (resource: Resource) => Promise<void> | void;
  onExportToGoogle?: (resource: Resource) => Promise<void> | void;
  dragProps: SortableListRenderProps;
}

const SortableResourceItem: React.FC<SortableResourceItemProps> = ({
  resource,
  canEdit,
  exportingResourceId,
  onOpen,
  onEdit,
  onDelete,
  onDuplicate,
  onExportToGoogle,
  dragProps,
}) => {
  const { setNodeRef, style, dragHandle } = dragProps;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const link = normalizeExternalResourceUrl(asLinkUrl(resource));
  const rowDisplay = getResourceRowDisplay(resource, link);

  return (
    <ListItem ref={setNodeRef} style={style} disablePadding divider>
      <ListItemButton onClick={() => onOpen(resource)} disableRipple>
        {dragHandle && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{dragHandle}</Box>
        )}
        <ListItemText
          sx={{ minWidth: 0, pr: 1 }}
          primary={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0, flexWrap: 'wrap' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700, minWidth: 0 }}>
                {resource.title}
              </Typography>
              <Chip
                size="small"
                icon={rowDisplay.chipIcon}
                label={rowDisplay.chipLabel}
                color={rowDisplay.chipColor}
                variant="outlined"
              />
            </Stack>
          }
          secondary={
            rowDisplay.secondaryText ? (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {rowDisplay.secondaryText}
              </Typography>
            ) : undefined
          }
        />
        <Stack direction="row" spacing={0.5} sx={{ ml: 1 }} onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                disabled={exportingResourceId === resource.id}
              >
                {exportingResourceId === resource.id ? (
                  <CircularProgress size={18} thickness={5} />
                ) : (
                  <MoreVertIcon fontSize="small" />
                )}
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    onEdit?.(resource);
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  Edit
                </MenuItem>
                {onDuplicate && resource.resourceKind !== 'file' && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      void onDuplicate(resource);
                    }}
                  >
                    <ListItemIcon>
                      <DuplicateIcon fontSize="small" />
                    </ListItemIcon>
                    Duplicate
                  </MenuItem>
                )}
                {onExportToGoogle &&
                  (resource.resourceKind === 'document' || resource.resourceKind === 'slide') && (
                    <MenuItem
                      disabled={Boolean(exportingResourceId)}
                      onClick={() => {
                        handleMenuClose();
                        void onExportToGoogle(resource);
                      }}
                    >
                      <ListItemIcon>
                        {exportingResourceId === resource.id ? (
                          <CircularProgress size={18} thickness={5} />
                        ) : (
                          <ExportIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      {exportingResourceId === resource.id
                        ? 'Exporting…'
                        : resource.resourceKind === 'slide'
                          ? 'Export to Google Slides'
                          : 'Export to Google Docs'}
                    </MenuItem>
                  )}
                {onDelete && (
                  <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => {
                      handleMenuClose();
                      void onDelete(resource.id);
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    Delete
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Stack>
      </ListItemButton>
    </ListItem>
  );
};

function getResourceRowDisplay(resource: Resource, link: string | null): ResourceRowDisplay {
  const description = resource.description?.trim() ?? '';

  if (resource.resourceKind === 'document') {
    return {
      chipLabel: 'Document',
      chipColor: 'default',
      chipIcon: <DescriptionIcon />,
      secondaryText: description,
      openTooltip: 'Open document',
    };
  }

  if (resource.resourceKind === 'slide') {
    return {
      chipLabel: 'Slide Deck',
      chipColor: 'default',
      chipIcon: <SlidesIcon />,
      secondaryText: description,
      openTooltip: 'Open slide editor',
    };
  }

  if (resource.resourceKind === 'file') {
    const content = resource.content as {
      originalFilename?: string;
      mimeType?: string;
      fileSize?: number;
    } | null;
    const filename = content?.originalFilename ?? 'File';
    const size =
      typeof content?.fileSize === 'number' ? `${(content.fileSize / 1024).toFixed(0)} KB` : '';
    return {
      chipLabel: 'File',
      chipColor: 'default',
      chipIcon: <DriveFileIcon />,
      secondaryText: description || `${filename}${size ? ` (${size})` : ''}`,
      openTooltip: 'View file',
    };
  }

  if (resource.resourceKind === 'rubric') {
    const rubricContent = asRubricContent(resource);
    const rubricType = rubricContent?.rubric?.rubricType ?? null;
    const chipIcon =
      rubricType === 'analytic' ? (
        <GridOnIcon />
      ) : rubricType === 'holistic' ? (
        <LinearScaleIcon />
      ) : rubricType === 'single-point' ? (
        <AdjustIcon />
      ) : (
        <RuleIcon />
      );
    const typeLabel =
      rubricType === 'analytic'
        ? 'Analytic Rubric'
        : rubricType === 'holistic'
          ? 'Holistic Rubric'
          : rubricType === 'single-point'
            ? 'Single-Point Rubric'
            : 'Rubric';
    return {
      chipLabel: typeLabel,
      chipColor: 'default',
      chipIcon,
      secondaryText: description,
      openTooltip: 'Open rubric',
    };
  }

  const googleWorkspaceLink = getGoogleWorkspaceLinkDisplay(link);
  if (googleWorkspaceLink) {
    return {
      chipLabel: googleWorkspaceLink.label,
      chipColor: googleWorkspaceLink.color,
      chipIcon: googleWorkspaceLink.icon,
      secondaryText: description || googleWorkspaceLink.secondaryText,
      openTooltip: googleWorkspaceLink.openTooltip,
    };
  }

  return {
    chipLabel: 'Link',
    chipColor: 'default',
    chipIcon: <LinkIcon />,
    secondaryText: description || link || '',
    openTooltip: 'Open link',
  };
}

function getGoogleWorkspaceLinkDisplay(link: string | null) {
  if (!link) return null;

  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase();
  const path = url.pathname.toLowerCase();

  if (host === 'docs.google.com') {
    if (path.includes('/document/')) {
      return {
        label: 'Google Doc',
        color: 'info' as const,
        icon: <GoogleDocIcon />,
        secondaryText: '',
        openTooltip: 'Open in Google Docs',
      };
    }

    if (path.includes('/presentation/')) {
      return {
        label: 'Google Slides',
        color: 'warning' as const,
        icon: <SlidesIcon />,
        secondaryText: '',
        openTooltip: 'Open in Google Slides',
      };
    }

    if (path.includes('/spreadsheets/')) {
      return {
        label: 'Google Sheet',
        color: 'success' as const,
        icon: <SheetsIcon />,
        secondaryText: '',
        openTooltip: 'Open in Google Sheets',
      };
    }
  }

  if (host === 'drive.google.com') {
    return {
      label: 'Drive File',
      color: 'default' as const,
      icon: <DriveFileIcon />,
      secondaryText: '',
      openTooltip: 'Open in Google Drive',
    };
  }

  return null;
}

function getResourceKindLabel(resourceKind: Resource['resourceKind']) {
  if (resourceKind === 'document') return 'Document';
  if (resourceKind === 'slide') return 'Slide Deck';
  if (resourceKind === 'file') return 'File';
  if (resourceKind === 'rubric') return 'Rubric';
  return 'Link';
}

function plainTextToTiptapDoc(text: string) {
  const lines = text.split('\n');
  return {
    type: 'doc',
    content: lines.map((line) =>
      line.length > 0
        ? {
            type: 'paragraph',
            content: [{ type: 'text', text: line }],
          }
        : {
            type: 'paragraph',
          }
    ),
  };
}

export default ResourceListCard;
